import { getCurrencySymbol } from '@fox/finance/src/helpers';
import { InputBase } from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import { MuiInputAdornment, NumberFormatCustom } from '../mui-text/index';
import { TextField } from '../text-field/index';

type CompProps = React.ComponentProps<typeof TextField>;

/** A text field that allows for input of decimal currency values.
 * Note it is an uncontrolled input in the @see NumberFormatCustom component   */
export const CurrencyInput: React.FC<
	{
		amount?: string;
		/** The amount as typed by the user - this will be effectively major currency */
		setAmount?: (a: string) => void;
		positiveOnly?: boolean;
	} & Pick<
		CompProps,
		| 'sx'
		| 'size'
		| 'fullWidth'
		| 'label'
		| 'onBlur'
		| 'defaultValue'
		| 'placeholder'
		| 'helperText'
		| 'status'
		| 'startAdornment'
		| 'disabled'
		| 'required'
	>
> = ({ amount, setAmount, size, label, sx, onBlur, defaultValue, placeholder, helperText, status, positiveOnly, ...rest }) => {
	return (
		<>
			<TextField
				label={label}
				fullWidth
				placeholder={placeholder}
				value={amount}
				InputLabelProps={!defaultValue && !amount && !!placeholder ? { shrink: true } : {}}
				defaultValue={defaultValue}
				size={size ?? undefined}
				sx={sx ?? undefined}
				helperText={helperText}
				onBlur={onBlur}
				onChange={e => {
					setAmount?.(e.target.value);
				}}
				status={status}
				InputProps={{
					inputComponent: NumberFormatCustom,
					inputProps: { positiveOnly }
				}}
				{...rest}
			/>
		</>
	);
};
const CurrencyStartAdornment = ({ currency = 'AUD' }: { currency?: string }) => (
	<MuiInputAdornment sx={{ height: 'auto' }} position="start">
		{getCurrencySymbol(currency)}
	</MuiInputAdornment>
);
export const CurrencyInputRaw = forwardRef<
	any,
	{
		// controlledUsage
		value?: string;
		// uncontrolled usage
		defaultValue?: string;
		label?: string;
		setAmount: (a: string) => void;
		size?: 'small' | 'medium';
		sx?: React.ComponentProps<typeof TextField>['sx'];
		onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
		onClick?: React.MouseEventHandler<HTMLInputElement>;
		autoFocus?: boolean;
		currency?: string;
	}
>(({ value, defaultValue, setAmount, size, onClick, autoFocus, sx, onBlur, currency }, ref) => {
	return (
		<>
			<InputBase
				inputRef={ref}
				autoFocus={autoFocus}
				onBlur={onBlur}
				onClick={onClick}
				value={value}
				defaultValue={defaultValue}
				size={size ?? undefined}
				sx={sx ?? undefined}
				onChange={e => {
					setAmount(e.target.value);
				}}
				startAdornment={<CurrencyStartAdornment currency={currency} />}
				inputComponent={NumberFormatCustom}
			/>
		</>
	);
});

export function digitsOnly(str: string) {
	return str.replace(/[^\d.]+/g, '');
}
export function digitsAndNegativeOnly(str: string) {
	return str.replace(/[^-\d.]+/g, '');
}

declare const __brand: unique symbol;
type Brand<B> = { [__brand]: B };
export type Branded<T, B> = T & Brand<B>;

export type CurrencyMinor = Branded<number, 'currency-minor'>;
export type CurrencyMajor = Branded<number, 'currency-major'>;
export type Cents = CurrencyMinor;
export type Dollars = CurrencyMajor;

//! TODO I am aware this duplicates some of the base currency code but for now this is our primary need - we should move all of this to @fox/financial soon
// TODO this should be explicit currency stuff
export function toCents(amountDollars: string | Dollars) {
	return Math.round(Number(typeof amountDollars === 'string' ? digitsAndNegativeOnly(amountDollars) : amountDollars) * 100) as CurrencyMinor;
}
export function toDollars(amountCents: Cents | number) {
	return Math.round(Number(amountCents) / 100) as Dollars;
}

/** A special branded value Cents for the zero amount */
export const ZERO_CENTS = 0 as Cents;

/** A special branded value for Dollars for the zero amount */
export const ZERO_DOLLARS = 0 as Dollars;

export function multiplyBranded<T extends number>(value: T, multiplier: number): T {
	return (value * multiplier) as T;
}
export class BrandMath {
	static round<T extends number>(n: T): T {
		return Math.round(n) as T;
	}
	static add<T extends number>(a: T, b: number): T {
		return (a + b) as T;
	}
	static sum<T extends number>(...a: T[]): T {
		return a.reduce((p, c) => this.add(p, c), 0 as T);
	}
	static subtract<T extends number>(a: T, b: T): T {
		return (a - b) as T;
	}
	static multiply<T extends number>(num: T, mul: number): T {
		return (num * mul) as T;
	}
	static max<T extends number>(...values: T[]): T {
		return Math.max(...values) as T;
	}
}

export function toCurrency(amountCents?: Cents) {
	if (!amountCents) return '';

	return String(amountCents / 100);
}

export const GridCurrencyInput = ({
	val,
	allowFractionalCurrency,
	currency,
	setAmount,
	style
}: {
	val: Dollars;
	allowFractionalCurrency?: boolean;
	currency?: string;
	setAmount: (amount: Dollars) => void;
	style?: React.CSSProperties;
}) => {
	const [mode, setMode] = useState(false);
	const [hover, setHover] = useState(false);

	useEffect(() => {
		if (!hover) return;

		const cancel = setTimeout(() => {
			setMode(true);
		}, 1000);
		return () => clearTimeout(cancel);
	}, [hover]);

	if (mode) {
		return (
			<GridCurrencyInputEditing
				ref={r => {
					if (r) {
						r.focus();
					}
				}}
				// onClick={e => e.target.select()}
				onBlur={() => setMode(false)}
				val={val}
				style={style}
				onChange={setAmount}
				allowFractionalCurrency={allowFractionalCurrency ?? false}
				currency={currency}
			/>
		);
	}

	const numDigits = 2;
	const maxDigits = allowFractionalCurrency ? numDigits + 4 : numDigits;
	return (
		<div
			style={{
				padding: '0.5em',
				...style
			}}
			onClick={() => requestAnimationFrame(() => setMode(true))}
			onMouseOver={() => setHover(true)}
			onMouseOut={() => setHover(false)}
		>
			{`${getCurrencySymbol(currency)} 
			${formatter(Number(val), numDigits, maxDigits)}`}
		</div>
	);
};

const formatter = (val: number, minDigits: number, maxDigits: number) => {
	return val.toLocaleString(undefined, { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits });
};

export const GridCurrencyInputEditing = forwardRef<
	any,
	{
		val: Dollars;
		allowFractionalCurrency: boolean;
		onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
		onChange: (amount: Dollars) => void;
		style?: React.CSSProperties;
		onClick?;
		currency?: string;
	}
>(({ val, allowFractionalCurrency, onChange, onBlur, style, onClick, currency }, ref) => {
	//TODO: Future work, get the currency in here (move to @fox/financial)
	const numDigits = 2;
	const maxDigits = allowFractionalCurrency ? numDigits + 4 : numDigits;
	return (
		<CurrencyInputRaw
			currency={currency}
			ref={ref}
			defaultValue={formatter(Number(val), numDigits, maxDigits)}
			onClick={onClick}
			onBlur={onBlur}
			sx={{ paddingLeft: '0.5rem', ...style }}
			setAmount={e => onChange(Number(e) as Dollars)}
		/>
	);
});
